/**
 * MONSTER TOWN — BUILD SYSTEM. OWNED BY THE BUILD CREW (src/build/**).
 *
 * The core verb of the game: put a thing down, move it, pick it up, undo it,
 * and never lose it. This module owns the grid mutations and the `placements`
 * save section; the gesture layer (gestures.ts), the preview (ghost.ts) and the
 * piece drawer (palette.ts) all funnel through the functions here.
 *
 * CONTRACTS THIS FILE KEEPS:
 *  - `grid.snapAnchor()` is the ONLY way a world point becomes an anchor.
 *  - After every occupy/release, `refreshConnected()` rebuilds neighbours' art
 *    (LAW #5, auto-connect).
 *  - Every instance group is tagged with `tagInstance()` so picking works.
 *  - Nothing is charged on placement: a kid buys the CATALOGUE ENTRY in the
 *    shop, then places it as many times as they like.
 *  - LOADING A SAVE NEVER DROPS A PIECE. Hydration bypasses the ownership check
 *    on purpose — an economy change must never silently delete a kid's house.
 */

import { bus } from '../core/events'
import { wallet } from '../core/economy'
import { Grid, rotatedSize, type Cell, type PlaceCheck, type Rot } from '../core/grid'
import { requirePlaceable } from '../core/placeable'
import { registerSaveSection, save } from '../core/save'
import { world, type SceneNode } from '../core/scene'
import type { PaletteName } from '../core/palette'
import type { InstanceId, PlaceableId, SceneId } from '../core/types'
import type { GameContext } from '../app/context'
import { clearStateOf, setStateOf, stateOf } from '../app/interactions'
import { placeArt, refreshConnected, removeArt } from './art'
import './events'
import { installGestures } from './gestures'
import { installGhost } from './ghost'
import { installPalette } from './palette'
import { installPlot } from './plot'
import { installBuildDemos } from './demo'
import { setBuildTool, tintKey, tints } from './state'

export {
  ensureSelection,
  rotateSelection,
  selectPlaceable,
  selectedPlaceable,
  selectionRot,
  buildTool,
  setBuildTool,
  toggleDeleteTool,
} from './state'

// ---------------------------------------------------------------------------
// state
// ---------------------------------------------------------------------------

let nextId = 1

interface UndoOp {
  kind: 'place' | 'remove' | 'move'
  sceneId: SceneId
  instanceId: InstanceId
  defId: PlaceableId
  anchor: Cell
  rot: Rot
  /** For 'move': where it was before. */
  fromAnchor?: Cell
  fromRot?: Rot
  state: Record<string, unknown>
  tint: PaletteName | null
  /** Ops sharing a group id undo together (a floor swap is one kid action). */
  group?: number
}
const undoStack: UndoOp[] = []
const UNDO_LIMIT = 40
let undoGroupSeq = 1

// ---------------------------------------------------------------------------
// place / remove / move
// ---------------------------------------------------------------------------

export interface PlaceOptions {
  /** No undo entry, no sound, no autosave — used by load and by demos. */
  silent?: boolean
  id?: InstanceId
  state?: Record<string, unknown>
  tint?: PaletteName
  /** Skip the ownership check. ONLY hydration and undo may set this. */
  force?: boolean
  /** Skip the support check. ONLY the last-resort hydration pass may set this. */
  ignoreSupport?: boolean
  /** Internal: tie this op to a sibling op so undo reverses both at once. */
  undoGroup?: number
}

/**
 * `grid.canPlace` plus ONE deliberate relaxation: a piece with a connect group
 * may also rest on its own kind sideways. That is what lets a kid roof the
 * middle of a room — the first roof tile sits on a wall, and every tile after
 * that leans on the one next to it. It cannot float in empty space, because it
 * still needs a same-group neighbour to hold on to.
 */
export function checkPlace(
  grid: Grid,
  defId: PlaceableId,
  anchor: Cell,
  rot: Rot,
  ignore?: InstanceId,
): PlaceCheck {
  const def = requirePlaceable(defId)
  const q = {
    anchor,
    footprint: def.footprint,
    rot,
    ...(ignore === undefined ? {} : { ignore }),
  }
  const first = grid.canPlace(q)
  if (first !== 'noSupport') return first
  const groupName = def.connect?.group
  if (groupName === undefined) return first
  if (grid.connectMask(anchor, groupName) === 0) return first
  return grid.canPlace({ ...q, requiresSupport: false })
}

export function place(
  sceneId: SceneId,
  defId: PlaceableId,
  anchor: Cell,
  rot: Rot = 0,
  opts: PlaceOptions = {},
): InstanceId | null {
  const node = world.get(sceneId)
  if (node === undefined) return null
  const def = requirePlaceable(defId)
  if (opts.force !== true && !wallet.canPlace(defId)) {
    bus.emit('build:rejected', { reason: 'notOwned', anchor })
    return null
  }
  const check =
    opts.ignoreSupport === true
      ? node.grid.canPlace({ anchor, footprint: def.footprint, rot, requiresSupport: false })
      : checkPlace(node.grid, defId, anchor, rot)
  if (check !== 'ok') {
    bus.emit('build:rejected', { reason: check, anchor })
    return null
  }

  const id = opts.id ?? (`i${nextId++}` as InstanceId)
  const placement = {
    instanceId: id,
    defId,
    anchor,
    rot,
    footprint: def.footprint,
    connectGroup: def.connect?.group ?? null,
    stackable: def.stackable,
    walkable: def.walkable,
  }
  node.grid.occupy(placement)
  setStateOf(sceneId, id, opts.state ?? def.initialState?.() ?? {})
  if (opts.tint !== undefined) tints.set(tintKey(sceneId, id), opts.tint)
  placeArt(node, id, defId, anchor, rot)
  refreshConnected(node, node.grid.footprintCells(anchor, def.footprint, rot))

  if (opts.silent !== true) {
    pushUndo({
      kind: 'place',
      sceneId,
      instanceId: id,
      defId,
      anchor,
      rot,
      state: {},
      tint: opts.tint ?? null,
      ...(opts.undoGroup === undefined ? {} : { group: opts.undoGroup }),
    })
    bus.emit('build:placed', { placement, sceneId, cost: 0 })
    bus.emit('audio:cue', { cue: 'place_snap' })
    save.request('build:place')
  }
  return id
}

export function removeInstance(sceneId: SceneId, id: InstanceId, silent = false, undoGroup?: number): boolean {
  const node = world.get(sceneId)
  if (node === undefined) return false
  const p = node.grid.placement(id)
  if (p === null) return false

  const cells = node.grid.footprintCells(p.anchor, p.footprint, p.rot)
  const state = { ...(stateOf(sceneId, id) ?? {}) }
  const tint = tints.get(tintKey(sceneId, id)) ?? null

  node.grid.release(id)
  removeArt(node, id)
  clearStateOf(sceneId, id)
  tints.delete(tintKey(sceneId, id))
  refreshConnected(node, cells)

  if (!silent) {
    pushUndo({
      kind: 'remove',
      sceneId,
      instanceId: id,
      defId: p.defId,
      anchor: p.anchor,
      rot: p.rot,
      state,
      tint,
      ...(undoGroup === undefined ? {} : { group: undoGroup }),
    })
    bus.emit('build:removed', { instanceId: id, defId: p.defId, sceneId, refund: 0 })
    bus.emit('audio:cue', { cue: 'pick_up' })
    save.request('build:remove')
  }
  return true
}

/**
 * Slide a piece to a new cell, keeping its identity, its state and its tint.
 * Returns false and leaves the world untouched if the destination is no good —
 * an invalid move simply does not commit, it is never an error (LAW #2).
 */
export function moveInstance(
  sceneId: SceneId,
  id: InstanceId,
  anchor: Cell,
  rot: Rot,
  silent = false,
): boolean {
  const node = world.get(sceneId)
  if (node === undefined) return false
  const p = node.grid.placement(id)
  if (p === null) return false
  const def = requirePlaceable(p.defId)
  if (checkPlace(node.grid, p.defId, anchor, rot, id) !== 'ok') return false

  const from = p.anchor
  const fromRot = p.rot
  const state = { ...(stateOf(sceneId, id) ?? {}) }
  const tint = tints.get(tintKey(sceneId, id)) ?? null
  const oldCells = node.grid.footprintCells(from, def.footprint, fromRot)

  node.grid.release(id)
  node.grid.occupy({
    instanceId: id,
    defId: p.defId,
    anchor,
    rot,
    footprint: def.footprint,
    connectGroup: def.connect?.group ?? null,
    stackable: def.stackable,
    walkable: def.walkable,
  })
  placeArt(node, id, p.defId, anchor, rot)
  refreshConnected(node, [...oldCells, ...node.grid.footprintCells(anchor, def.footprint, rot)])

  if (!silent) {
    pushUndo({
      kind: 'move',
      sceneId,
      instanceId: id,
      defId: p.defId,
      anchor,
      rot,
      fromAnchor: from,
      fromRot,
      state,
      tint,
    })
    bus.emit('build:moved', { instanceId: id, from, to: anchor, rot })
    bus.emit('audio:cue', { cue: 'place_snap' })
    save.request('build:move')
  }
  return true
}

// ---------------------------------------------------------------------------
// anchors — where a tap actually lands
// ---------------------------------------------------------------------------

/** Ground cover: things you stand ON rather than stack on (floors, paths, rugs). */
export function isGroundCover(defId: PlaceableId): boolean {
  const def = requirePlaceable(defId)
  return def.walkable && def.footprint.h === 1 && !def.stackable
}

/**
 * Lowest free level for a whole footprint — the max over every base column, so
 * a 2x2 piece dropped half on a block lands on top of the block rather than
 * clipping through it.
 */
export function landingLevel(
  grid: Grid,
  anchor: Cell,
  defId: PlaceableId,
  rot: Rot,
  ignore?: InstanceId,
): number {
  const def = requirePlaceable(defId)
  const { w, d } = rotatedSize(def.footprint, rot)
  let gy = 0
  for (let dz = 0; dz < d; dz++) {
    for (let dx = 0; dx < w; dx++) {
      gy = Math.max(gy, topLevelIgnoring(grid, anchor.gx + dx, anchor.gz + dz, ignore))
    }
  }
  return gy
}

/**
 * THE PIECE IN YOUR HAND DOES NOT HOLD ITSELF UP (fixed r3).
 *
 * `grid.topLevelAt` counts every occupied cell in a column, including the cells
 * belonging to the piece a kid is currently carrying — it is still in the grid
 * on purpose, so a dropped touch can never lose it. The result: pick a block up
 * and the preview jumped one LEVEL, onto the top of the very block in your hand,
 * where it has no support once that block is ignored. So the first thing a kid
 * saw after pressing on their own block was a red "won't fit" pad floating above
 * it, and putting it straight back down was refused.
 *
 * Peel the carried piece off the top of the column and the landing is the tile
 * it came from — which is exactly what "put it back" should mean.
 */
function topLevelIgnoring(grid: Grid, gx: number, gz: number, ignore?: InstanceId): number {
  let gy = grid.topLevelAt(gx, gz)
  if (ignore === undefined) return gy
  while (gy > 0 && grid.at({ gx, gy: gy - 1, gz }) === ignore) gy--
  return gy
}

/**
 * The level a connecting piece WANTS to be on: the one where it already has a
 * neighbour of its own kind. This is why roofing a house is three taps rather
 * than a lesson — put one roof tile on top of a wall, then keep tapping and
 * every following tile joins that roof instead of landing on the floor.
 * Returns null when there is nothing of its kind nearby.
 */
function connectLevel(grid: Grid, defId: PlaceableId, flat: Cell, rot: Rot): number | null {
  const def = requirePlaceable(defId)
  const groupName = def.connect?.group
  if (groupName === undefined) return null
  // LOWEST first, deliberately: a wall next to a wall belongs on the ground,
  // not one storey up just because that level also touches the neighbour.
  for (let gy = 0; gy < grid.spec.levels; gy++) {
    const anchor: Cell = { gx: flat.gx, gy, gz: flat.gz }
    if (grid.connectMask(anchor, groupName) === 0) continue
    if (grid.canPlace({ anchor, footprint: def.footprint, rot, requiresSupport: false }) === 'ok') {
      return gy
    }
  }
  return null
}

export interface Landing {
  anchor: Cell
  valid: boolean
  /** A ground-cover tile that would be repainted by this placement. */
  swaps: InstanceId | null
}

/**
 * THE tap rule. Given a world point and what is selected, work out exactly
 * where the piece would go and whether it fits. Used by the ghost and by the
 * placement path so what a kid sees is exactly what they get.
 */
export function landingFor(
  node: SceneNode,
  defId: PlaceableId,
  groundX: number,
  groundZ: number,
  rot: Rot,
  ignore?: InstanceId,
): Landing {
  const def = requirePlaceable(defId)
  const grid = node.grid
  const flat = grid.clampAnchor(grid.snapAnchor(groundX, groundZ, def.footprint, rot, 0), def.footprint, rot)

  if (isGroundCover(defId)) {
    // Floors and paths paint the ground layer: they never stack, and dropping
    // one on another floor simply repaints that tile (undoable, one action).
    const anchor = { ...flat, gy: 0 }
    const existing = grid.placementAt(anchor)
    if (existing !== null && existing.instanceId !== ignore) {
      const swappable = isGroundCover(existing.defId) && existing.defId !== defId
      return { anchor, valid: swappable, swaps: swappable ? existing.instanceId : null }
    }
    return {
      anchor,
      valid: checkPlace(grid, defId, anchor, rot, ignore) === 'ok',
      swaps: null,
    }
  }

  const gy = connectLevel(grid, defId, flat, rot) ?? landingLevel(grid, flat, defId, rot, ignore)
  const anchor = grid.clampAnchor({ ...flat, gy }, def.footprint, rot)
  return { anchor, valid: checkPlace(grid, defId, anchor, rot, ignore) === 'ok', swaps: null }
}

/** Place at a world point using the tap rule. Returns the new id, or null. */
export function placeAtPoint(
  sceneId: SceneId,
  defId: PlaceableId,
  groundX: number,
  groundZ: number,
  rot: Rot,
): InstanceId | null {
  const node = world.get(sceneId)
  if (node === undefined) return null
  const landing = landingFor(node, defId, groundX, groundZ, rot)
  if (!landing.valid) {
    bus.emit('build:rejected', { reason: 'noFit', anchor: landing.anchor })
    return null
  }
  if (landing.swaps !== null) {
    // Never tear the old tile up until we know the new one can actually go
    // down, or a kid loses a floor tile and gets nothing back for it.
    if (!wallet.canPlace(defId)) {
      bus.emit('build:rejected', { reason: 'notOwned', anchor: landing.anchor })
      return null
    }
    const g = undoGroupSeq++
    removeInstance(sceneId, landing.swaps, false, g)
    return place(sceneId, defId, landing.anchor, rot, { undoGroup: g })
  }
  return place(sceneId, defId, landing.anchor, rot)
}

// ---------------------------------------------------------------------------
// undo — always available, always one tap away. Never a dead end.
// ---------------------------------------------------------------------------

function pushUndo(op: UndoOp): void {
  undoStack.push(op)
  if (undoStack.length > UNDO_LIMIT) undoStack.shift()
}

function undoOne(op: UndoOp): void {
  if (op.kind === 'place') {
    removeInstance(op.sceneId, op.instanceId, true)
  } else if (op.kind === 'remove') {
    place(op.sceneId, op.defId, op.anchor, op.rot, {
      silent: true,
      force: true,
      id: op.instanceId,
      state: op.state,
      ...(op.tint === null ? {} : { tint: op.tint }),
    })
  } else {
    const from = op.fromAnchor ?? op.anchor
    const fromRot = op.fromRot ?? op.rot
    moveInstance(op.sceneId, op.instanceId, from, fromRot, true)
  }
}

export function undo(): boolean {
  const op = undoStack.pop()
  if (op === undefined) return false
  undoOne(op)
  // A floor repaint is one kid action made of two grid ops — reverse both.
  let steps = 1
  while (op.group !== undefined && undoStack.length > 0) {
    const peek = undoStack[undoStack.length - 1]
    if (peek === undefined || peek.group !== op.group) break
    undoStack.pop()
    undoOne(peek)
    steps++
  }
  bus.emit('build:undo', { steps })
  bus.emit('audio:cue', { cue: 'undo_pop' })
  save.request('build:undo')
  return true
}

export function undoDepth(): number {
  return undoStack.length
}

export function clearUndo(): void {
  undoStack.length = 0
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

export function installBuild(ctx: GameContext): void {
  registerSaveSection<SavedPlacements>(
    {
      id: 'placements',
      version: 1,
      defaults: () => ({ nextId: 1, scenes: {} }),
      serialize: serializePlacements,
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const scenes: Record<string, SavedInstance[]> = {}
        const rawScenes = c.isRecord(raw['scenes']) ? raw['scenes'] : {}
        for (const [sceneId, list] of Object.entries(rawScenes)) {
          scenes[sceneId] = c.arr<SavedInstance>(list, (e) => {
            if (!c.isRecord(e)) return null
            const defId = c.str(e['d'], '')
            if (defId === '') return null
            return {
              i: c.str(e['i'], ''),
              d: defId,
              x: c.num(e['x'], 0),
              y: c.num(e['y'], 0),
              z: c.num(e['z'], 0),
              r: (Math.max(0, Math.min(3, Math.round(c.num(e['r'], 0)))) as Rot),
              s: c.isRecord(e['s']) ? e['s'] : {},
              t: typeof e['t'] === 'string' ? (e['t'] as PaletteName) : null,
            }
          })
        }
        return { nextId: Math.max(1, c.num(raw['nextId'], 1)), scenes }
      },
    },
    applyPlacements,
  )

  // TRASH IS NEVER INHERITED. Delete is a mode a kid enters on purpose and it
  // must not outlive the moment they chose it: arming the bin, tapping the
  // hammer out to play and tapping it back used to leave `data-tool="delete"`
  // still set, so the next tap on the town took a piece away that nobody meant
  // to take away. Leaving build mode, or walking into another room, always
  // hands the hammer back in PLACE mode. (Undo still covers the mis-tap — this
  // just stops a 3-year-old being in a destructive mode they did not choose.)
  bus.on('app:modeChanged', () => setBuildTool('place'))
  bus.on('scene:entered', () => setBuildTool('place'))
  /*
   * AN UNDO IS AN "OOPS", SO IT HANDS THE BIN BACK (fixed r3).
   *
   * Round-3 critic, major: the trash tool "stays armed after an undo … and the
   * only way out is a second tap on the same trash button; there is nothing
   * indicating that". A kid who has just undone something is in the one moment
   * where another accidental delete costs the most, so undo always returns the
   * hammer to its safe mode. Getting back into the bin is one big red tap; there
   * is nothing to hunt for and nothing is lost either way.
   */
  bus.on('build:undo', () => setBuildTool('place'))

  installGhost(ctx)
  installGestures(ctx)
  installPalette(ctx)
  installPlot(ctx)
  installBuildDemos(ctx)
}

// ---------------------------------------------------------------------------
// persistence — SAVES ARE SACRED
// ---------------------------------------------------------------------------

interface SavedInstance {
  i: string
  d: string
  x: number
  y: number
  z: number
  r: Rot
  s: Record<string, unknown>
  t: PaletteName | null
}
interface SavedPlacements {
  nextId: number
  scenes: Record<string, SavedInstance[]>
}

function serializePlacements(): SavedPlacements {
  const scenes: Record<string, SavedInstance[]> = {}
  for (const sceneId of world.ids()) {
    const node = world.require(sceneId)
    const list: SavedInstance[] = []
    for (const p of node.grid.all()) {
      list.push({
        i: p.instanceId,
        d: p.defId,
        x: p.anchor.gx,
        y: p.anchor.gy,
        z: p.anchor.gz,
        r: p.rot,
        s: stateOf(sceneId, p.instanceId) ?? {},
        t: tints.get(tintKey(sceneId, p.instanceId)) ?? null,
      })
    }
    if (list.length > 0) scenes[sceneId] = list
  }
  return { nextId, scenes }
}

function applyPlacements(data: SavedPlacements): void {
  nextId = data.nextId
  // CLEAR EVERY SCENE FIRST, not just the ones present in the incoming data.
  // Otherwise switching to a kid's EMPTY slot leaves the other kid's town
  // standing, and the next autosave writes it into their slot. This is exactly
  // how a town gets lost; the two-slot drive step exists to catch it.
  for (const sceneId of world.ids()) {
    const node = world.require(sceneId)
    for (const p of node.grid.all()) clearStateOf(sceneId, p.instanceId)
    node.grid.clear()
    for (const [, obj] of node.art) node.placeables.remove(obj)
    node.art.clear()
  }
  tints.clear()

  for (const [sceneId, list] of Object.entries(data.scenes)) {
    const node = world.get(sceneId as SceneId)
    if (node === undefined) continue
    const id = sceneId as SceneId

    const put = (s: SavedInstance, ignoreSupport: boolean): boolean =>
      place(id, s.d as PlaceableId, { gx: s.x, gy: s.y, gz: s.z }, s.r, {
        silent: true,
        // NEVER let an economy/ownership change delete a kid's house on load.
        force: true,
        ...(ignoreSupport ? { ignoreSupport: true } : {}),
        id: s.i as InstanceId,
        state: s.s,
        ...(s.t === null ? {} : { tint: s.t }),
      }) !== null

    // Lowest level first so stacked pieces normally find their support on the
    // first pass. Then keep sweeping while anything still lands: a roof tile
    // may be waiting on the roof tile next to it, in any order. Whatever is
    // still homeless after that is placed WITHOUT the support check, because
    // "we could not work out what held it up" must never mean "we deleted it".
    let pending = [...list].filter((s) => s.i !== '').sort((a, b) => a.y - b.y)
    for (let pass = 0; pass < 8 && pending.length > 0; pass++) {
      const next = pending.filter((s) => !put(s, false))
      if (next.length === pending.length) break
      pending = next
    }
    for (const s of pending) put(s, true)

    for (const s of list) {
      const n = Number.parseInt(s.i.replace(/\D/g, ''), 10)
      if (Number.isFinite(n) && n >= nextId) nextId = n + 1
    }
  }
  clearUndo()
}

/** Harness/debug: drop a demo layout into a scene. */
export function placeAll(
  sceneId: SceneId,
  items: readonly { defId: PlaceableId; anchor: Cell; rot?: Rot; tint?: PaletteName }[],
): void {
  for (const it of items) {
    place(sceneId, it.defId, it.anchor, it.rot ?? 0, {
      silent: true,
      force: true,
      ...(it.tint === undefined ? {} : { tint: it.tint }),
    })
  }
  save.request('demoLayout')
}

/** Exposed for tests: the grid of the active scene. */
export function activeGrid(): Grid | null {
  const id = world.activeSceneId
  return id === null ? null : world.require(id).grid
}
